import {
  AgraphwealthportfolioManagementproposalsv1ListResolvedOrdersResponse,
  V1RefreshOrderAttributesRequestBody,
  V1RefreshOrderAttributesResponse,
} from '@blk/aladdin-graph-client-axios-internal/wealth/portfolio_management/proposals/v1/resolved_order_api';
import { CancelToken } from 'axios';
import { AggregateTreeParams, ProposalSupplementParams } from '../AWBridge/Proposal/v1/Proposal';
import { awPortfolioManagementApi } from './Base';
import {
  AGraphAggregateTree,
  AGraphProposal,
  AGraphProposalSupplement,
  AGraphProposalSupplementsResponse,
  BatchGetProposalsRequestBody,
  BatchGetProposalsResponseBody,
  BatchUpdateWorkflowStatusesRequestBody,
  BatchUpdateWorkflowStatusesResponse,
  GetResolvedOrdersRequestBody,
  IBatchUpdateWorkflowStatusesResponse,
  IProposalAggregateSummariesRequestBody,
  IProposalAggregateSummariesResponseBody,
  ListUserProposalSummariesRequestBody,
  ListUserProposalSummariesResponseBody,
  ProposalRequest,
  ProposalSummaryRequestBody,
  ProposalSummaryResponseBody,
  RefreshOrderAttributesParams,
  UpdateWorkflowStatusRequestBody,
} from './types';

const BASE_PROPOSALS_PATH = 'proposals/v1/proposals';

export function getAGraphProposalSupplement(params: ProposalSupplementParams, cancelToken?: CancelToken) {
  const { proposalId, supplementId, pkg } = params;
  const path = `${BASE_PROPOSALS_PATH}/${proposalId}/supplements/${supplementId}`;
  return awPortfolioManagementApi.get<AGraphProposalSupplement>(path, { params: { pkg }, cancelToken });
}

export function getAGraphProposalSupplements(
  params: Omit<ProposalSupplementParams, 'supplementId'>,
  cancelToken?: CancelToken
) {
  const { proposalId, pkg } = params;
  const path = `${BASE_PROPOSALS_PATH}/${proposalId}/supplements`;
  return awPortfolioManagementApi.get<AGraphProposalSupplementsResponse>(path, { params: { pkg }, cancelToken });
}

export function getAGraphAggregateTree(params: AggregateTreeParams, cancelToken?: CancelToken) {
  const { aggIDValue, aggIDTag, pkg, aggregateAsOf, aggregateOrigin, accountID, accountAsOf, accountOrigin } = params;
  const path = `portfolios/v1/aggregates:resolveAggregateTrees`;
  return awPortfolioManagementApi.get<AGraphAggregateTree>(path, {
    params: {
      pkg,
      'aggregate.origin': aggregateOrigin,
      'aggregate.asOfTime': aggregateAsOf,
      'aggregate.id': aggIDValue,
      'aggregate.tag': aggIDTag,
      'account.asOfTime': accountAsOf,
      'account.id': accountID,
      'account.origin': accountOrigin,
    },
    cancelToken,
  });
}

export function createAGraphProposalSupplement(
  proposalSupplement: Required<Pick<Partial<AGraphProposalSupplement>, 'id' | 'proposalId'>>,
  pkg: string
) {
  const path = `${BASE_PROPOSALS_PATH}/${proposalSupplement.proposalId}/supplements`;
  return awPortfolioManagementApi.post<AGraphProposalSupplement>(path, proposalSupplement, { params: { pkg } });
}

export function updateAGraphProposalSupplement(
  proposalSupplement: Required<Pick<Partial<AGraphProposalSupplement>, 'id' | 'proposalId'>>,
  pkg: string,
  cancelToken?: CancelToken
) {
  const path = `${BASE_PROPOSALS_PATH}/${proposalSupplement.proposalId}/supplements/${proposalSupplement.id}`;
  return awPortfolioManagementApi.put<AGraphProposalSupplement>(path, proposalSupplement, {
    cancelToken,
    params: { pkg },
  });
}

export function postFilterProposalsBySummary(
  proposalSummaryRequestBody: ProposalSummaryRequestBody,
  cancelToken?: CancelToken
) {
  return awPortfolioManagementApi.post<ProposalSummaryResponseBody>(
    `${BASE_PROPOSALS_PATH}:filterBySummary`,
    proposalSummaryRequestBody,
    { cancelToken, params: { pkg: proposalSummaryRequestBody.pkg } }
  );
}

export function postlistUserProposalSummaries(
  listUserProposalSummaryRequestBody: ListUserProposalSummariesRequestBody,
  cancelToken?: CancelToken
) {
  return awPortfolioManagementApi.post<ListUserProposalSummariesResponseBody>(
    `${BASE_PROPOSALS_PATH}:listUserProposalSummaries`,
    listUserProposalSummaryRequestBody,
    { cancelToken, params: { pkg: listUserProposalSummaryRequestBody.pkg } }
  );
}

export function getAgraphProposal(params: ProposalRequest, cancelToken?: CancelToken) {
  const { id, resolveProposedAggregate, pkg } = params;
  const path = `${BASE_PROPOSALS_PATH}/${id}`;
  return awPortfolioManagementApi.get<AGraphProposal>(path, { cancelToken, params: { resolveProposedAggregate, pkg } });
}

export function getAgraphProposalAggregateSummaries(
  proposalAggregateSummariesRequestBody: IProposalAggregateSummariesRequestBody,
  cancelToken?: CancelToken
) {
  return awPortfolioManagementApi.post<IProposalAggregateSummariesResponseBody>(
    `${BASE_PROPOSALS_PATH}:aggregateSummaries`,
    proposalAggregateSummariesRequestBody,
    { cancelToken, params: { pkg: proposalAggregateSummariesRequestBody.pkg } }
  );
}

export function postBatchUpdateWorkflowStatuses(
  batchUpdateWorkflowStatusesRequestBody: BatchUpdateWorkflowStatusesRequestBody,
  cancelToken?: CancelToken
) {
  const path = `${BASE_PROPOSALS_PATH}:batchUpdateWorkflowStatuses`;
  return awPortfolioManagementApi.post<IBatchUpdateWorkflowStatusesResponse>(
    path,
    batchUpdateWorkflowStatusesRequestBody,
    { cancelToken }
  );
}

export function postUpdateWorkflowStatus(
  updateWorkflowStatusRequestBody: UpdateWorkflowStatusRequestBody,
  cancelToken?: CancelToken
) {
  const { pkg, assignee, creationTime, proposalId, userId, fixedComment, status, userComment } =
    updateWorkflowStatusRequestBody;
  const path = `${BASE_PROPOSALS_PATH}/${proposalId}/workflowStatus`;
  return awPortfolioManagementApi.post<BatchUpdateWorkflowStatusesResponse>(
    path,
    { creationTime, fixedComment, status, userComment, userId },
    { params: { pkg, assignee }, cancelToken }
  );
}

export function postBatchGetProposals(
  batchGetProposalsRequestBody: BatchGetProposalsRequestBody,
  cancelToken?: CancelToken
) {
  const path = `${BASE_PROPOSALS_PATH}:batchGet`;
  return awPortfolioManagementApi.post<BatchGetProposalsResponseBody>(path, batchGetProposalsRequestBody, {
    cancelToken,
  });
}

const BASE_PROPOSALS_V1_PATH = 'proposals/v1';

export function getResolvedOrders(params: GetResolvedOrdersRequestBody, cancelToken?: CancelToken) {
  const path = `${BASE_PROPOSALS_V1_PATH}/resolvedOrders`;
  const { pkg, proposalId } = params;
  return awPortfolioManagementApi.get<AgraphwealthportfolioManagementproposalsv1ListResolvedOrdersResponse>(path, {
    params: { pkg, proposalId },
    cancelToken,
  });
}

export function refreshResolvedOrderAttributes(
  params: RefreshOrderAttributesParams,
  body: V1RefreshOrderAttributesRequestBody,
  cancelToken?: CancelToken
) {
  const path = `${BASE_PROPOSALS_V1_PATH}/resolvedOrders:refreshOrderAttributes`;
  const { pkg, proposalId } = params;
  return awPortfolioManagementApi.post<V1RefreshOrderAttributesResponse>(path, body, {
    params: { pkg, proposalId },
    cancelToken,
  });
}
